import { useApi } from "@platform/api";
import {
  listBenefitPlanGroups,
  useGetAvailableCoverages,
  useLoadRfp,
  type BenefitPlanGroup,
} from "@platform/btx-plan-designer";
import {
  MOVE_PROPOSAL_PLAN_GROUP_MUTATION_KEY,
  proposalQueryKeys,
  REQUOTE_ACTION_MUTATION_KEY,
  useProposals,
  type ProposalScope,
} from "@platform/mpx-quotes-grid";
import { CoverageTypeEnum } from "@platform/types";
import {
  useIsFetching,
  useIsMutating,
  useQueries,
} from "@tanstack/react-query";
import { useMemo } from "react";

import { useRfpDetails } from "../../../../../../../hooks/useRfpDetails";
import { proposalsPollInterval } from "../utils/awaitingCarrierQuotes";
import { filterToRequestedCoverageTypes } from "../utils/requestedCoverageTypes";

import { useCoverageSelection } from "./useCoverageSelection";
import { useProposalsWithCarrierNames } from "./useProposalsWithCarrierNames";
import { useRefetchProposalsOnLocChange } from "./useRefetchProposalsOnLocChange";
import { useUnmappedPlansPages } from "./useUnmappedPlansPages";

// Before `rfp` itself has loaded, `rfp?.coverages` is `[]`, so
// `benefitPlanGroupQueries` has zero entries and `.some(...)` is vacuously
// false. And before `rfpDetails` resolves, `ecoRfpId` is `""`, which leaves
// `useLoadRfp` disabled — a disabled query reports `isLoading: false` too.
// Include both upstream loads so this stays "loading" through every
// pre-data tick, not just after each query object exists and is enabled.
const computeIsLoadingBenefitPlanGroups = (
  isLoadingDetails: boolean,
  isLoadingRfp: boolean,
  benefitPlanGroupQueries: { isPending: boolean; data: unknown }[],
): boolean =>
  isLoadingDetails ||
  isLoadingRfp ||
  benefitPlanGroupQueries.some((query) => query.isPending && !query.data);

// The proposals query is disabled until ecoRfpId resolves, so its own
// isLoading can read false before ecoRfpId is even known — include the
// upstream loads so this stays "loading" through that tick too.
const computeIsLoadingProposals = (
  isLoadingDetails: boolean,
  isLoadingRfp: boolean,
  isLoadingProposalsQuery: boolean,
): boolean => isLoadingDetails || isLoadingRfp || isLoadingProposalsQuery;

// useCoverageSelection derives selectedBpgId from benefitPlanGroupsByCoverage,
// which is still empty while its query is in flight. Without this, a fresh
// nav with no ?loc=/?bpg= params can pass the loading gate with a coverage
// type selected but no plan group yet, rendering RatesGrid/SummaryGrid with
// an empty Requested column. Stay loading until the default plan group
// resolves, when the coverage genuinely has one.
const computeIsResolvingDefaultPlan = (
  selectedCoverageType: CoverageTypeEnum | undefined,
  selectedBpgId: string | undefined,
  benefitPlanGroupsByCoverage: Partial<Record<CoverageTypeEnum, unknown[]>>,
): boolean =>
  !!selectedCoverageType &&
  !selectedBpgId &&
  (benefitPlanGroupsByCoverage[selectedCoverageType]?.length ?? 0) > 0;

const computeIsLoadingQuotes = (flags: {
  isLoadingDetails: boolean;
  isLoadingRfp: boolean;
  isLoadingProposals: boolean;
  isLoadingBenefitPlanGroups: boolean;
  isResolvingDefaultPlan: boolean;
}): boolean =>
  flags.isLoadingDetails ||
  flags.isLoadingRfp ||
  flags.isLoadingProposals ||
  flags.isLoadingBenefitPlanGroups ||
  flags.isResolvingDefaultPlan;

const getBenefitPlanGroupsError = (
  queries: { error: Error | null }[],
): Error | null => queries.find((query) => query.error)?.error ?? null;

interface BenefitPlanGroupQueryState {
  data: BenefitPlanGroup[] | undefined;
  isPending: boolean;
  error: Error | null;
}

// useQueries returns a new result array on every render unless it's given a
// stable `combine`, whose output React Query structurally shares. Every grid
// memo downstream keys on these plan groups, so an unstable array rebuilt the
// whole Quotes grid on any re-render, even a fetch flag flipping.
const combineBenefitPlanGroupQueries = (
  queries: BenefitPlanGroupQueryState[],
): BenefitPlanGroupQueryState[] =>
  queries.map(({ data, isPending, error }) => ({ data, isPending, error }));

// ---------------------------------------------------------------------------
// useQuotesData — data fetching, coverage selection, and grid computation
// ---------------------------------------------------------------------------

export const useQuotesData = (
  rfpId: string | undefined,
  proposalScope: ProposalScope,
  initialCoverageType?: CoverageTypeEnum,
  initialBpgId?: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- inferred; extracted interface would be large
) => {
  const axiosInstance = useApi();

  const {
    data: rfpDetails,
    isLoading: isLoadingDetails,
    error: rfpDetailsError,
  } = useRfpDetails({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
  });

  const ecoRfpId = rfpDetails?.ecoRfpId ?? "";

  const {
    data: rfp,
    isLoading: isLoadingRfp,
    error: rfpError,
  } = useLoadRfp(ecoRfpId, axiosInstance, {
    enabled: !!ecoRfpId,
  });

  const benefitPlanGroupQueries = useQueries({
    queries: (rfp?.coverages ?? []).map((coverage) => ({
      queryKey: [
        "quotes-benefit-plan-groups",
        ecoRfpId,
        coverage.type,
        coverage.productType ?? "",
      ],
      queryFn: () =>
        listBenefitPlanGroups(
          {
            rfpId: ecoRfpId,
            coverageType: coverage.coverageType,
            productType: coverage.productType,
          },
          axiosInstance,
        ),
      enabled: !!ecoRfpId && !!coverage.coverageType,
    })),
    combine: combineBenefitPlanGroupQueries,
  });

  const benefitPlanGroupsByCoverage = useMemo(
    () =>
      (rfp?.coverages ?? []).reduce<
        Partial<Record<CoverageTypeEnum, BenefitPlanGroup[]>>
      >((result, coverage, index) => {
        result[coverage.type] = benefitPlanGroupQueries[index]?.data ?? [];
        return result;
      }, {}),
    [benefitPlanGroupQueries, rfp?.coverages],
  );

  const {
    data: rawAvailableCoverageTypes,
    isLoading: isLoadingAvailableCoverageTypes,
  } = useGetAvailableCoverages(ecoRfpId, axiosInstance);

  const isLoadingBenefitPlanGroups = computeIsLoadingBenefitPlanGroups(
    isLoadingDetails,
    isLoadingRfp,
    benefitPlanGroupQueries,
  );

  const movingPlanGroupCount = useIsMutating({
    mutationKey: MOVE_PROPOSAL_PLAN_GROUP_MUTATION_KEY,
  });

  const {
    data: proposals = [],
    isLoading: isLoadingProposalsQuery,
    isFetching: isFetchingProposals,
    isPlaceholderData: isProposalsPlaceholder,
    error: proposalsError,
    refetch: refetchProposals,
  } = useProposals(
    ecoRfpId,
    {
      enabled: !!ecoRfpId,
      // Carrier API responses land server-to-server with no client event, so
      // poll while any invited carrier hasn't answered — new quotes and their
      // rates then render without a manual page refresh.
      refetchInterval: (query) =>
        proposalsPollInterval(query.state.data, movingPlanGroupCount > 0),
    },
    proposalScope,
  );

  const isLoadingProposals = computeIsLoadingProposals(
    isLoadingDetails,
    isLoadingRfp,
    isLoadingProposalsQuery,
  );

  // Narrowed to what was actually requested of at least one carrier (CD-2371) — a coverage
  // no proposal ever requested has no dropdown entry, and (per useUnmappedPlansPages below)
  // no unmapped-plan badge either, since there'd be no menu entry to click through to it.
  const availableCoverageTypes = useMemo(
    () => filterToRequestedCoverageTypes(rawAvailableCoverageTypes, proposals),
    [rawAvailableCoverageTypes, proposals],
  );

  const proposalsWithCarrierNames = useProposalsWithCarrierNames(
    rfpId,
    proposals,
  );

  const {
    benefitPlanGroupsByCoverage: benefitPlanGroupsByCoverageWithUnmapped,
    unmappedCountByCoverage,
    carrierNamesWithUnmappedPlans,
  } = useUnmappedPlansPages(
    proposalsWithCarrierNames,
    benefitPlanGroupsByCoverage,
    availableCoverageTypes,
  );

  const {
    selectedCoverageType,
    selectedBpgId,
    selectedBenefitPlanGroup,
    handlePlanSelect,
  } = useCoverageSelection({
    rfpCoverages: rfp?.coverages,
    availableCoverageTypes,
    benefitPlanGroupsByCoverage: benefitPlanGroupsByCoverageWithUnmapped,
    initialCoverageType,
    initialBpgId,
  });

  const selectedCoverage = useMemo(
    () =>
      rfp?.coverages.find(
        (coverage) => coverage.type === selectedCoverageType,
      ) ?? rfp?.coverages[0],
    [rfp?.coverages, selectedCoverageType],
  );

  useRefetchProposalsOnLocChange(selectedCoverageType, refetchProposals);

  const benefitPlanGroupsError = getBenefitPlanGroupsError(
    benefitPlanGroupQueries,
  );

  const isResolvingDefaultPlan = computeIsResolvingDefaultPlan(
    selectedCoverageType,
    selectedBpgId,
    benefitPlanGroupsByCoverage,
  );

  const isLoading = computeIsLoadingQuotes({
    isLoadingDetails,
    isLoadingRfp,
    isLoadingProposals,
    isLoadingBenefitPlanGroups,
    isResolvingDefaultPlan,
  });

  const duplicatingPlanGroupCount = useIsMutating({
    mutationKey: ["duplicateProposalPlanGroup"],
  });

  // Replace / Disregard / Restore. Counting the action itself — not just the
  // proposal refetch it triggers — is what makes the grid respond the moment the
  // analyst confirms the dialog, rather than sitting idle for the length of the
  // action request and only then starting to load.
  const requoteActionCount = useIsMutating({
    mutationKey: REQUOTE_ACTION_MUTATION_KEY,
  });

  const fetchingProposalsCount = useIsFetching({
    queryKey: proposalQueryKeys.all(ecoRfpId),
  });

  const isRefetchingProposals =
    (isFetchingProposals && isProposalsPlaceholder) ||
    duplicatingPlanGroupCount > 0 ||
    requoteActionCount > 0 ||
    fetchingProposalsCount > 0;
  const error =
    rfpDetailsError ?? rfpError ?? proposalsError ?? benefitPlanGroupsError;
  const hasData = !!ecoRfpId && !!rfp?.coverages.length && !!selectedCoverage;

  return {
    axiosInstance,
    rfpId,
    ecoRfpId,
    isLoading,
    isRefetchingProposals,
    error,
    hasData,
    selectedCoverage,
    selectedCoverageType,
    selectedBpgId,
    proposals: proposalsWithCarrierNames,
    benefitPlanGroupsByCoverage: benefitPlanGroupsByCoverageWithUnmapped,
    availableCoverageTypes,
    isLoadingAvailableCoverageTypes,
    unmappedCountByCoverage,
    carrierNamesWithUnmappedPlans,
    selectedBenefitPlanGroup,
    handlePlanSelect,
  };
};
